import React, { createContext, useContext, useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signOut, signInWithRedirect } from 'aws-amplify/auth';
import amplifyConfig from '../config/amplify';
import axios from 'axios';

// Configure Amplify
Amplify.configure(amplifyConfig);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState(null);
  const [approved, setApproved] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const session = await fetchAuthSession();
      
      if (session.tokens?.idToken) {
        const token = session.tokens.idToken.toString();
        setIdToken(token);
        
        // Get user profile from backend
        const response = await axios.get(`${API}/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setUser(response.data);
        setApproved(response.data.approved);
        
        // Check admin status
        try {
          const adminCheck = await axios.get(`${API}/auth/admin/check`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          setIsAdmin(adminCheck.data.is_admin);
        } catch (error) {
          // If admin check fails, user is not admin
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setIdToken(null);
        setApproved(false);
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setIdToken(null);
      setApproved(false);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (provider = 'Google') => {
    try {
      await signInWithRedirect({ provider });
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setIdToken(null);
      setApproved(false);
      setIsAdmin(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    user,
    loading,
    idToken,
    approved,
    isAdmin,
    login,
    logout,
    isAuthenticated: !!user,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};